import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ScienceIcon from '@mui/icons-material/Science';
import UndoIcon from '@mui/icons-material/Undo';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { wz } from '../../components/wizard-tokens';
import { MiSpinner, WizCard, WizGradientButton } from '../../components/wizard-ui';
import {
  candidateInitials,
  isCompanyBlocked,
  testButtonLabelKey,
  testErrorText,
  testSubtitle,
} from './flow-view.logic';
import { useFlowTest } from './use-flow-test';

type FlowTest = ReturnType<typeof useFlowTest>;

const NoteBox: React.FC<{
  children: React.ReactNode;
  color?: string;
  spinner?: boolean;
  padding?: number;
}> = ({ children, color = wz.muted, spinner }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      fontSize: 14,
      color,
      background: wz.page,
      borderRadius: 10,
      padding: 14,
    }}
  >
    {spinner ? <MiSpinner size={20} /> : null}
    {children}
  </div>
);

const Result: React.FC<{ test: FlowTest }> = ({ test }) => {
  const { t } = useTranslation();
  const { result } = test;
  if (!result) return null;
  return (
    <div
      style={{
        border: `1px solid ${wz.border}`,
        borderRadius: 10,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div
        style={{
          fontSize: 13,
          color: wz.muted,
          marginBottom: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
        {t('wizMsgTestFor', { name: result.candidateName })}
      </div>
      {result.messages.map((m, i) => (
        <div key={m.n} style={{ display: 'flex', gap: 14 }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: 24,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: wz.primarySoft,
                color: wz.primary,
                fontSize: 12,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {m.n}
            </div>
            {i < result.messages.length - 1 ? (
              <div style={{ flex: 1, width: 1, background: wz.border, margin: '4px 0' }} />
            ) : null}
          </div>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              paddingBottom: 14,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{m.type}</div>
              <div style={{ fontSize: 12, color: wz.muted }}>{m.when}</div>
            </div>
            <div
              style={{
                background: wz.page,
                borderRadius: 10,
                padding: '12px 14px',
                fontSize: 14,
                lineHeight: 1.55,
                color: wz.textDim,
                whiteSpace: 'pre-line',
              }}
            >
              {m.text.trim() ? m.text : t('wizMsgNoText')}
              {m.problem ? (
                <div style={{ fontSize: 12, color: wz.muted, marginTop: 4 }}>{m.problem}</div>
              ) : null}
            </div>
          </div>
        </div>
      ))}
      {result.end ? (
        <div
          style={{
            fontSize: 13,
            color: wz.text555,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            margin: '0 0 8px 38px',
          }}
        >
          <UndoIcon sx={{ fontSize: 16, color: wz.muted }} />
          {result.end}
        </div>
      ) : null}
      <div style={{ fontSize: 12, color: wz.muted, display: 'flex', alignItems: 'center', gap: 4 }}>
        <AutoAwesomeIcon sx={{ fontSize: 14, color: wz.primary }} />
        {result.source === 'ai' ? t('wizMsgTestSourceAi') : t('wizMsgTestSourceTemplate')}
      </div>
    </div>
  );
};

/** Karte „Flow testen“: Profil und Szenario wählen, Ablauf von der KI schreiben lassen (nichts wird gesendet). */
const FlowTestCard: React.FC<{ test: FlowTest }> = ({ test }) => {
  const { t } = useTranslation();
  const { status } = test;
  const hasCandidates = test.candidates.length > 0;

  return (
    <WizCard gap={18}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{t('wizMsgTestTitle')}</div>
          <div style={{ fontSize: 14, color: wz.muted }}>{t('wizMsgTestSubtitle')}</div>
        </div>
        <div style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
          <WizGradientButton
            onClick={() => void test.run()}
            disabled={!test.canRun}
            icon={<ScienceIcon sx={{ fontSize: 18 }} />}
          >
            <span style={{ whiteSpace: 'nowrap' }}>{t(testButtonLabelKey(status))}</span>
          </WizGradientButton>
        </div>
      </div>

      {hasCandidates ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 10 }}>
          {test.candidates.map(c => {
            const selected = test.candidateId === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => test.pickCandidate(c.id)}
                aria-pressed={selected}
                style={{
                  borderRadius: 10,
                  padding: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  color: wz.text,
                  border: selected ? `1.5px solid ${wz.primary}` : `1px solid ${wz.border}`,
                  background: selected ? wz.selectedBg : '#FFF',
                  opacity: isCompanyBlocked(c) ? 0.5 : 1,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: wz.primarySoft,
                    color: wz.primary,
                    fontSize: 13,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {candidateInitials(c.name)}
                </div>
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{c.name}</div>
                  <div
                    style={{
                      fontSize: 12,
                      color: wz.muted,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {testSubtitle(c, t('wizMsgBlocked'))}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <NoteBox spinner={test.candidatesLoading}>
          {test.candidatesLoading ? t('wizMsgCandidatesLoading') : t('wizMsgNoCandidates')}
        </NoteBox>
      )}

      {status === 'loading' ? (
        <NoteBox spinner color={wz.text555}>
          {test.phase === 'preparing' ? t('wizMsgTestPreparing') : t('wizMsgTestLoading')}
        </NoteBox>
      ) : null}
      {status === 'stale' ? <NoteBox>{t('wizMsgTestStale')}</NoteBox> : null}
      {status === 'error' ? (
        <NoteBox color={wz.redFg}>{testErrorText(t('wizMsgTestError'), test.error)}</NoteBox>
      ) : null}
      {status === 'done' ? <Result test={test} /> : null}
    </WizCard>
  );
};

export default FlowTestCard;
